"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Crown,
  Sparkles,
  ShieldCheck,
  Lock,
  Eye,
  Building2,
  Users,
  Award,
  ArrowRight,
  Mic,
  Compass,
  Briefcase,
  Heart,
  BookOpen,
  KeyRound,
  Wifi,
  Activity,
  Zap,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "0", suffix: "", label: "Security Incidents", sub: "Fifteen-plus years", accent: "purple" },
  { metric: "99.9999", suffix: "%", label: "Operational Uptime", sub: "31.5 sec max/year", accent: "cyan" },
  { metric: "85", suffix: "-95%", label: "Crisis Success Rate", sub: "Crisis Response Engine deployment", accent: "amber-gold" },
  { metric: "384", suffix: "x-1,416x", label: "Speed Advantage", sub: "Over traditional crisis", accent: "red" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "Across all deployments", accent: "emerald" },
  { metric: "15", suffix: "+", label: "Years Discreet", sub: "Zero information leakage", accent: "denim" },
];

const CHALLENGES = [
  {
    icon: Heart,
    title: "Reputation Protection",
    body: "Royal reputation is not a branding exercise. It is the perception fabric that sustains public consent for the institution&apos;s existence. Unlike corporate brands that can reposition, royal reputation operates within narrow historical corridors where missteps become permanent record.",
    accent: "purple",
  },
  {
    icon: Lock,
    title: "Privacy Enforcement",
    body: "Sovereign households require communication security that exceeds what most government agencies demand. The intersection of personal life, state business, and dynastic strategy means a single intercepted communication can expose diplomatic negotiations, financial arrangements, family disputes, or succession planning.",
    accent: "cyan",
  },
  {
    icon: Wifi,
    title: "Sovereign Digital Infrastructure",
    body: "Royal households depend on digital systems at the intersection of personal convenience and state security. The challenge is building systems where the household&apos;s digital life is invisible to external observation while remaining operationally seamless.",
    accent: "emerald",
  },
  {
    icon: BookOpen,
    title: "Legacy Narrative Management",
    body: "Every royal institution manages a narrative that predates its current members by centuries and will outlast them by centuries more. Legacy narrative management requires the same strategic rigor as long-term infrastructure planning, but applied to perception rather than concrete.",
    accent: "amber-gold",
  },
];

const PLATFORMS = [
  {
    title: "Crisis Response Engine",
    subtitle: "Reputation Protection Engine",
    body: "Detects reputational threats before they materialize and neutralizes them at three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional crisis management. The Golden Hour Principle: ninety-five percent of crisis damage occurs in the first six hours.",
    metrics: [
      { label: "Crisis Success", value: "85-95%" },
      { label: "Detection Latency", value: "Sub-second" },
      { label: "Recovery", value: "30-60 days" },
    ],
    accent: "red",
  },
  {
    title: "Security Operations Layer",
    subtitle: "Sovereign Security Architecture",
    body: "The zero-trust security platform that has never been breached. Seven-layer defense-in-depth with quantum-resistant cryptography. Air-gapped deployment capability for households requiring complete network isolation.",
    metrics: [
      { label: "Uptime", value: "99.9999%" },
      { label: "Incidents", value: "Zero" },
      { label: "Detection Rate", value: "99.7%" },
    ],
    accent: "purple",
  },
  {
    title: "Narrative Engineering Layer",
    subtitle: "Narrative Control Engine",
    body: "Engineers and controls how the world sees the royal institution across every digital surface. Deploys content across fifty-plus digital platforms with nine-vector platform-specific adaptation, achieving ninety-five to ninety-eight percent organic authenticity.",
    metrics: [
      { label: "Narrative Lift", value: "300-500%" },
      { label: "Platforms", value: "50+" },
      { label: "Authenticity", value: "95-98%" },
    ],
    accent: "cyan",
  },
  {
    title: "CryptoSuite",
    subtitle: "Encrypted Personal Communications",
    body: "Encrypted communication infrastructure that secures personal and household communications across all modalities. Zero-knowledge architecture where even platform operators cannot access content. Complete metadata elimination at the protocol level.",
    metrics: [
      { label: "Encryption", value: "Post-Quantum" },
      { label: "Metadata", value: "Zero" },
      { label: "Group Size", value: "1,000" },
    ],
    accent: "amber-gold",
  },
];

const SERVICES = [
  { title: "Reputation Analysis", body: "Comprehensive assessment of the royal institution&apos;s perception posture across two hundred-plus digital platforms, one hundred thousand-plus news sources, and one thousand-plus dark web sources. Intelligence Operations Layer maps every narrative thread, every sentiment cluster, every influence network.", accent: "purple" },
  { title: "Crisis Management", body: "When reputational threats materialize, automated containment deploys through five hundred-plus pre-built response playbooks. Operations at three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional crisis management, compressing the critical damage window by ninety-nine point nine percent.", accent: "red" },
  { title: "Brand Protection", body: "Continuous monitoring and defense of the royal institution&apos;s digital identity across all surfaces. Tracking every mention, every association, every search result, every narrative thread. Counter-narratives and authority-building content ensure the digital presence reflects intended positioning.", accent: "cyan" },
  { title: "Encrypted Communications", body: "End-to-end encrypted communication channels across all modalities with complete metadata elimination. Zero-knowledge architecture secures every conversation between family members, advisors, legal counsel, and diplomatic contacts.", accent: "emerald" },
  { title: "Sovereign Infrastructure", body: "The security architecture that protects the household&apos;s entire digital infrastructure — from network-level encryption to data-at-rest protection to endpoint security. Seven independent security layers with no single point of failure.", accent: "amber-gold" },
  { title: "Counter-Surveillance", body: "Active detection and neutralization of surveillance attempts targeting the royal household. Monitoring for unusual packet patterns, DNS tunneling, IMSI catcher deployment, physical surveillance devices, and behavioral anomalies that indicate monitoring.", accent: "denim" },
  { title: "Digital Footprint Minimization", body: "Systematic reduction of the household&apos;s digital attack surface — closing orphaned accounts, removing obsolete infrastructure, managing data broker relationships, scrubbing personal information from commercial databases.", accent: "lime" },
  { title: "Legacy Narrative Engineering", body: "Long-term strategic narrative management that ensures the institution&apos;s story remains coherent, dignified, and historically resilient across generational transitions, constitutional crises, and cultural shifts.", accent: "coral" },
];

const DISCRETION = [
  { icon: Lock, title: "Compartmentalized Operations", body: "Operations Coordination Layer enables compartmentalized operations where every team sees only what they need to see, and the unified command sees everything. No single point of failure can expose the institution&apos;s complete strategic posture." },
  { icon: KeyRound, title: "Zero-Knowledge Architecture", body: "Data is encrypted on the client device before reaching any server. Even the infrastructure operators cannot access client data. No backdoors, no escrow keys, no exceptions. The household&apos;s communications remain confidential against external adversaries and infrastructure providers alike." },
  { icon: ShieldCheck, title: "Binding Confidentiality Agreements", body: "Every individual who operates within sovereign deployments signs binding confidentiality agreements that enforce secrecy across all parties. Legally enforceable instruments that create personal liability for any disclosure of client information." },
  { icon: Wifi, title: "Air-Gap Deployment Capability", body: "For households requiring complete network isolation, full operational functionality without internet connectivity. Local threat detection and signature updates via physical media with cryptographic verification. Critical for environments where encrypted traffic represents unacceptable risk." },
];

export default function KingdomsRoyalFamiliesPage() {
  return (
    <>
      <PageHero
        icon={Crown}
        overline="Royal Families & Dynasties"
        overlineClassName="text-purple"
        title={
          <>
            Absolute Discretion. <span className="text-gradient-premium">Verified Outcomes.</span>
          </>
        }
        subtitle="Royal courts and sovereign households where reputation is generational, privacy is existential, and discretion is non-negotiable. Dr. Jyoti Kush deploys integrated reputation protection, privacy enforcement, and sovereign digital infrastructure — at national security standards, with verified outcomes traceable to fifteen-plus years of sovereign-level deployments."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Royal Families" }]}
        badge="National security level confidentiality"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Reputation and privacy at sovereign scale.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Royal families face a class of challenges that no corporate entity, political campaign, or public figure encounters at comparable intensity. Their challenges are not episodic — they are perpetual, generational, and intertwined with the identity of nations. A corporate CEO&apos;s reputation crisis ends when the CEO departs. A royal reputation crisis persists across reigns, across centuries, across the collective memory of populations.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The operational architecture addresses four dimensions as an integrated system. Reputation without privacy is exposure. Privacy without infrastructure is fragile. Infrastructure without legacy narrative is purposeless. The system operates across all four simultaneously, coordinated through the neural command interface that achieves ninety-five percent coordination success across all concurrent platform operations.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations that engage Dr. Jyoti Kush do not receive advice. They receive verified results. They do not trade in promises. They trade in outcomes. The same operational discipline that protects classified military communications protects royal households — with absolute discretion and zero information leakage.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Crown className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Royal Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Four interlocking dimensions of royal operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The core challenges facing royal courts and sovereign households form an interlocking system where each vulnerability amplifies the others. The architecture addresses all four simultaneously.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CHALLENGES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <c.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Briefcase className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Sovereign Technology Stack</span>
          </div>
          <h2 className="text-display-md mb-4">Four platforms. One operational fabric.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each platform was built in-house — not licensed, not white-labeled, not assembled from third-party components. They operate on proprietary infrastructure that took a decade to build and cannot be replicated without that same decade of investment.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PLATFORMS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <Eye className="size-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-heading-md ${a.text}`}>{p.title}</h3>
                    <span className="text-overline text-muted-foreground">{p.subtitle}</span>
                  </div>
                </div>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{p.body}</p>
                <div className="grid grid-cols-3 gap-2">
                  {p.metrics.map((m) => (
                    <div key={m.label} className={`p-2.5 rounded-lg bg-background/30 border ${a.border}`}>
                      <div className={`text-caption font-bold ${a.text}`}>{m.value}</div>
                      <div className="text-caption text-muted-foreground">{m.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Services Portfolio</span>
          </div>
          <h2 className="text-display-md mb-4">Eight services across reputation, privacy, and infrastructure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Royal sector engagements deploy across two primary pillars of the Penta P Framework — Perception, Reputation & Public Relations and Privacy, Encryption & Information Security — operating as an integrated system.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {SERVICES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Lock className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Discretion Standard</span>
          </div>
          <h2 className="text-display-md mb-4">National security level confidentiality.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The discretion standard exceeds what most government agencies require. This is not a policy statement. It is an architectural commitment — embedded in the technology stack, enforced through operational protocols, and verified through fifteen-plus years of zero security incidents.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {DISCRETION.map((d, i) => {
            const accents = ["purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <d.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{d.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <span className="text-overline text-purple">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Royal Service Pillars.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight pillars that compose the integrated reputation, privacy, and infrastructure capability deployed for sovereign households. Each pillar operates as one dimension of a single closed-loop system.
          </p>
        </div>
        <PillarGridBlock
          columns={4}
          variant="numbered"
          pillars={[
            { title: "Reputation Sovereignty", body: "Complete control over the household's public narrative across 200+ digital platforms and 100,000+ news sources.", icon: <Heart className="size-5" />, metric: "300-500%", metricLabel: "Narrative lift", accent: "purple" },
            { title: "Privacy Inviolability", body: "Communications security with metadata elimination, zero-knowledge architecture, and customer-controlled keys.", icon: <Lock className="size-5" />, metric: "Zero", metricLabel: "Knowledge", accent: "cyan" },
            { title: "Legacy Preservation", body: "Generational narrative management ensuring institutional coherence across reigns, crises, and cultural shifts.", icon: <BookOpen className="size-5" />, metric: "100%", metricLabel: "Continuity", accent: "amber-gold" },
            { title: "Crisis Transformation", body: "Crisis containment at 384x-1,416x the speed of traditional approaches, with 85-95% success rates.", icon: <Sparkles className="size-5" />, metric: "85-95%", metricLabel: "Success rate", accent: "red" },
            { title: "Encrypted Communications", body: "End-to-end encrypted channels across voice, text, video, and data with complete metadata elimination.", icon: <KeyRound className="size-5" />, metric: "1,000", metricLabel: "Group size", accent: "emerald" },
            { title: "Sovereign Infrastructure", body: "Seven-layer defense-in-depth with quantum-resistant encryption and air-gap deployment capability.", icon: <ShieldCheck className="size-5" />, metric: "7", metricLabel: "Security layers", accent: "denim" },
            { title: "Counter-Surveillance", body: "Active detection and neutralization of surveillance attempts targeting the household across digital and physical vectors.", icon: <Eye className="size-5" />, metric: "24/7", metricLabel: "Monitoring", accent: "lime" },
            { title: "Digital Footprint Minimization", body: "Systematic reduction of the household's digital attack surface across data brokers, orphaned accounts, and obsolete infrastructure.", icon: <Wifi className="size-5" />, metric: "Zero", metricLabel: "Trace leakage", accent: "coral" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Verified Discretion Record</span>
          </div>
          <h2 className="text-display-md mb-4">The standard of absolute discretion.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            These metrics trace to operational data across 15+ years of sovereign-level deployments. The record is not aspirational — it is the verified outcome of architecture that exceeds what most government agencies require.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 0, suffix: "", label: "Security Incidents", description: "Across all deployments in 15+ years", accent: "purple" },
            { value: 99.9999, suffix: "%", label: "Operational Uptime", description: "Maximum 31.5 seconds of downtime per year", accent: "cyan" },
            { value: 100, suffix: "%", label: "Client Satisfaction", description: "Across 300+ elite clients served", accent: "emerald" },
            { value: 15, suffix: "+", label: "Years Discreet", description: "Zero information leakage across sovereign deployments", accent: "amber-gold" },
            { value: 18, suffix: "", label: "Active Countries", description: "Middle East, GCC, Southern Asia, Central Asia, Africa, Europe", accent: "red" },
            { value: 300, suffix: "+", label: "Elite Clients", description: "Royal households, executives, and public figures served", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <span className="text-overline text-amber-gold">Engagement Lifecycle</span>
          </div>
          <h2 className="text-display-md mb-4">Five-phase sovereign assessment to perpetual operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Royal sector engagements do not follow project timelines. They operate as perpetual partnerships because the challenges facing royal institutions are perpetual. The engagement lifecycle unfolds across five distinct phases.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "Sovereign Assessment", body: "Weeks 1-4. CLAIRVOYANCE CX maps the household's complete digital posture. S3-SENTINEL conducts full security audit. Deliverables: Perception Baseline Report and Security Posture Assessment.", icon: <Eye className="size-5" />, accent: "purple" },
            { title: "Strategic Architecture", body: "Weeks 5-8. LITHVIK N1's strategic architecture engine generates the operational blueprint specifying which platforms activate, which teams deploy, and which audiences receive which messages.", icon: <Compass className="size-5" />, accent: "cyan" },
            { title: "Platform Activation", body: "Weeks 9-12. S3-SENTINEL establishes security foundation first. CryptoSuite deploys. PHOENIX-1 activates. PERCEPTION X2 initiates narrative engineering campaigns.", icon: <Zap className="size-5" />, accent: "emerald" },
            { title: "Operational Operations", body: "Ongoing. CLAIRVOYANCE CX monitors in real-time. PHOENIX-1 watches for emerging threats. PERCEPTION X2 shapes narrative environment. Monthly executive briefs from LITHVIK N1.", icon: <Activity className="size-5" />, accent: "amber-gold" },
            { title: "Perpetual Strengthening", body: "Continuous. Each threat detected strengthens the early warning system. Each crisis averted validates the response playbooks. The system compounds with each cycle.", icon: <Award className="size-5" />, accent: "red" },
            { title: "The Six Principles", body: "Command, Control, Converge, Conceal, Calculate, Conquer — the operating discipline enforced across every royal sector engagement with no exceptions.", icon: <Sparkles className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Royal and dynastic engagements begin with the discretion standard that exceeds what most government agencies require. National security level confidentiality from first contact. Zero information leakage. The standard is absolute.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Presidential & PM Offices", description: "Strategic command at the highest level.", href: "/sectors/presidential-pm-offices/", icon: Award, accent: "amber-gold" },
          { title: "Public Figures & HNWIs", description: "Reputation and privacy for prominent individuals.", href: "/sectors/public-figures-hnwis/", icon: Users, accent: "coral" },
          { title: "Governments", description: "Governance transformation at sovereign scale.", href: "/sectors/governments/", icon: Building2, accent: "red" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: Crown, accent: "purple" },
        ]}
      />
    </>
  );
}
